// Intl throws on a zone it does not know, and the zone comes from user preferences: without the catch one bad zone blanks the screen mid-render.
const formatter = (timeZone?: string): Intl.DateTimeFormat => {
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone });
  } catch {
    return new Intl.DateTimeFormat('en-US', options);
  }
};

const datePart = (iso: string, timeZone?: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  const parts = formatter(timeZone).formatToParts(date);
  return (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';
};

export const shortDate = (iso: string, timeZone?: string): string => {
  const parts = datePart(iso, timeZone);
  return parts ? `${parts('day')} ${parts('month')} ${parts('year')}` : iso;
};

const dayMonth = (iso: string, timeZone?: string): string => {
  const parts = datePart(iso, timeZone);
  return parts ? `${parts('day')} ${parts('month')}` : iso;
};

// 00-rules: "3 Sep" this year, "3 Sep 2025" before it — a date with no time of day.
export const calendarDay = (iso: string, now: number, timeZone?: string): string => {
  const parts = datePart(iso, timeZone);
  const thisYear = datePart(new Date(now).toISOString(), timeZone);
  if (!parts || !thisYear) return iso;
  return parts('year') === thisYear('year') ? dayMonth(iso, timeZone) : shortDate(iso, timeZone);
};

const SIZE_UNITS = ['kB', 'MB', 'GB', 'TB'] as const;

// 00-rules: "1.8 kB · 23.2 kB · 45.0 GB" — decimal units, one decimal, bytes whole.
export const sizeText = (bytes: number): string => {
  if (bytes < 1000) return `${Math.round(bytes)} B`;
  let value = bytes / 1000;
  let unit = 0;
  while (value >= 1000 && unit < SIZE_UNITS.length - 1) {
    value /= 1000;
    unit += 1;
  }
  return `${value.toFixed(1)} ${SIZE_UNITS[unit]}`;
};

// 00-rules: "$42.00" — always cents, grouped thousands.
export const usdText = (amount: number): string =>
  `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const clockTime = (iso: string, timeZone?: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
  try {
    return new Intl.DateTimeFormat('en-GB', { ...options, timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  }
};

// Compared as a zone-local string, never as Date objects: those compare instants, and "same day" is a question about somebody's calendar.
const dayKey = (date: Date, timeZone?: string): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }
};

export const edgeTime = (iso: string, now: number, timeZone?: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const clock = clockTime(iso, timeZone);
  const today = dayKey(new Date(now), timeZone);
  const day = dayKey(date, timeZone);
  if (day === today) return `${clock} today`;

  const yesterday = dayKey(new Date(now - 86_400_000), timeZone);
  if (day === yesterday) return `${clock} yesterday`;
  return `${clock} · ${shortDate(iso, timeZone)}`;
};

// 00-rules: "15:42" today, "23 Sep 15:42" on other days, "23 Sep 2025" last year and older.
export const pointTime = (iso: string, now: number, timeZone?: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const day = dayKey(date, timeZone);
  const today = dayKey(new Date(now), timeZone);
  if (day === today) return clockTime(iso, timeZone);
  if (day.slice(0, 4) !== today.slice(0, 4)) return shortDate(iso, timeZone);
  return `${dayMonth(iso, timeZone)} ${clockTime(iso, timeZone)}`;
};

export const clockSeconds = (iso: string, timeZone?: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };
  try {
    return new Intl.DateTimeFormat('en-GB', { ...options, timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  }
};

export interface ITimeSpan {
  from: string;
  to: string;
}

// Every unknown answers true: a caller with no window, or an unreadable one, is exactly the case where the day must still be printed.
export const spansDays = (span: ITimeSpan | null, timeZone?: string): boolean => {
  if (!span) return true;

  const from = new Date(span.from);
  const to = new Date(span.to);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return true;
  return dayKey(from, timeZone) !== dayKey(to, timeZone);
};

export const logTime = (iso: string, span: ITimeSpan | null, timeZone?: string): string => {
  if (Number.isNaN(new Date(iso).getTime())) return iso;

  const clock = clockSeconds(iso, timeZone);
  return spansDays(span, timeZone) ? `${dayMonth(iso, timeZone)} ${clock}` : clock;
};

export const rangeTime = (
  startIso: string,
  endIso: string,
  now: number,
  timeZone?: string,
): string => {
  if (!spansDays({ from: startIso, to: endIso }, timeZone)) {
    return `${clockTime(startIso, timeZone)} – ${edgeTime(endIso, now, timeZone)}`;
  }

  const day = (iso: string) => `${dayMonth(iso, timeZone)} ${clockTime(iso, timeZone)}`;
  return `${day(startIso)} – ${day(endIso)}`;
};

export const relativeTime = (
  iso: string | null | undefined,
  now: number,
  timeZone?: string,
): string => {
  if (!iso) return 'never';

  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;

  const seconds = Math.round((now - then) / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;

  return shortDate(iso, timeZone);
};

export const untilTime = (
  iso: string | null | undefined,
  now: number,
  timeZone?: string,
): string => {
  if (!iso) return 'not scheduled';

  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;

  const seconds = Math.round((then - now) / 1000);
  if (seconds <= 0) return 'due now';
  if (seconds < 60) return 'in under a minute';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `in ${minutes} min`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours} hour${hours === 1 ? '' : 's'}`;

  const days = Math.round(hours / 24);
  if (days === 1) return 'tomorrow';
  if (days < 30) return `in ${days} days`;

  return shortDate(iso, timeZone);
};

// 00-rules: "318 ms · 1.9 s · 7 min · 2 h 10 min · 1 d 14 h" — never "days" spelled out.
export const spanText = (ms: number): string => {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))} ms`;
  if (ms < 60_000) {
    const seconds = ms / 1000;
    return seconds < 10 ? `${Number(seconds.toFixed(1))} s` : `${Math.round(seconds)} s`;
  }
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 24 * 60) {
    const rest = minutes % 60;
    return rest ? `${Math.floor(minutes / 60)} h ${rest} min` : `${minutes / 60} h`;
  }
  const hours = Math.round(minutes / 60);
  const rest = hours % 24;
  return rest ? `${Math.floor(hours / 24)} d ${rest} h` : `${hours / 24} d`;
};

export const browserZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const zoneOffset = (date: Date, timeZone: string): string => {
  try {
    const part = new Intl.DateTimeFormat('en-GB', { timeZone, timeZoneName: 'shortOffset' })
      .formatToParts(date)
      .find((candidate) => candidate.type === 'timeZoneName')?.value;
    return part ? part.replace('GMT', 'UTC') : '';
  } catch {
    return '';
  }
};

export const fullTime = (iso: string, timeZone?: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const zone = timeZone ?? browserZone();
  const offset = zoneOffset(date, zone);
  return `${shortDate(iso, zone)}, ${clockSeconds(iso, zone)} · ${zone}${offset ? ` (${offset})` : ''}`;
};

// 00-rules: whole percent, one decimal below 1%; a real share never reads 0.0%.
export const shareText = (part: number, whole: number): string => {
  if (whole <= 0 || part <= 0) return '0%';
  const percent = (part / whole) * 100;
  if (percent >= 1) return `${Math.round(percent)}%`;
  return percent < 0.1 ? 'under 0.1%' : `${percent.toFixed(1)}%`;
};
